import React, { useEffect, useState } from 'react';
import { supabase, handleOAuthRedirect } from '../lib/supabaseClient';

/**
 * OAuth callback page for popup auth flow.
 * Processes tokens → broadcasts session to parent via postMessage → closes.
 */
export default function AuthCallbackPage() {
  const [status, setStatus] = useState('Completing sign-in...');

  useEffect(() => {
    const complete = async () => {
      let finalSession = null;

      try {
        // Process OAuth tokens from URL hash
        const session = await handleOAuthRedirect();
        if (session) {
          finalSession = session;
        } else {
          const { data: { session: existing } } = await supabase.auth.getSession();
          if (existing) {
            finalSession = existing;
          }
        }
      } catch (err) {
        console.error('[AuthCallback] Error processing tokens:', err);
      }

      if (finalSession) {
        setStatus('Signed in! Syncing session...');

        // Broadcast session to parent window (iframe or opener)
        const sessionPayload = {
          type: 'SUPABASE_AUTH_SUCCESS',
          access_token: finalSession.access_token,
          refresh_token: finalSession.refresh_token,
        };

        const origin = window.location.origin;

        // Try opener (popup → parent)
        if (window.opener) {
          try {
            window.opener.postMessage(sessionPayload, origin);
            console.log('[AuthCallback] Session broadcasted to opener');
          } catch (err) {
            console.warn('[AuthCallback] Failed to postMessage to opener:', err);
          }
        }

        // Also try parent (in case of nested iframe)
        if (window.parent && window.parent !== window) {
          try {
            window.parent.postMessage(sessionPayload, origin);
            console.log('[AuthCallback] Session broadcasted to parent');
          } catch (err) {
            console.warn('[AuthCallback] Failed to postMessage to parent:', err);
          }
        }

        setStatus('Signed in! This window will close...');
      } else {
        setStatus('Sign-in completed. You can close this window.');
      }

      // Close popup after giving postMessage time to deliver
      setTimeout(() => {
        try { window.close(); } catch {}
      }, 1200);
    };

    complete();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-3">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}
